import {
  CustomDocumentFields,
  DOCUMENT_SENDER_ID,
  IDocument,
  IDocumentAttachment,
  IDocumentTemplate,
  RecipientEntityTypeEnum,
  RedirectSettings,
  TemplateBuilderAppStore,
  TimeZoneEnum,
  isValidUrl,
  validateEmail,
} from '@gohighlevel/ghl-proposals-common'
import { isEmpty, isNil, uniqBy } from 'lodash'

import i18n from '@gohighlevel/ghl-proposals-common/src/locales'
import { defineStore } from 'pinia'
import { toRaw } from 'vue'

export const useAppStore = defineStore('appStore', {
  state: (): TemplateBuilderAppStore => ({
    email: '',
    userId: '',
    apiKey: '',
    tokenId: '',
    iamToken: '',
    companyId: '',
    timeZone: '' as TimeZoneEnum,
    userName: '',
    parentBaseUrl: '',
    handshake: null as unknown as Promise<any>,
    requestRetryCount: 0,
    document: {
      enableDirectPayment: false,
      enableAutoSendInvoice: false,
      paymentLiveMode: true,
      notificationSettings: {},
    } as IDocument,
    unsavedChanges: false,
    currency: 'USD',
    locationStaticCustomValues: [],
    locationCustomValues: [],
    locationCustomFields: [],
    userData: {} as Promise<any>,
    linkReference: [],
    whiteLabelViewerBaseUrl: '',
    locale: 'en-US',
    isInternal: false,
    invoiceWhiteLabelBaseUrl: '',
    docFormPublicLink: '',
    sendAttempted: false,
  }),
  getters: {
    flatDocumentVariables: state => {
      return state.document.variables.reduce((acc, variable) => {
        return {
          ...acc,
          [variable.fieldKey]: variable.value,
        }
      })
    },
  },
  actions: {
    updateDocumentVariable({ fieldKey, value, type }: any) {
      const index = this.document.variables.findIndex(
        variable => variable.fieldKey === fieldKey
      ) as number
      if (index === -1) {
        this.document.variables.push({ fieldKey, value, type })
      } else {
        this.document.variables[index].value = value
      }
      this.setUnsavedChanges(true)
    },
    deleteDocumentCustomVariable(value: string) {
      this.document.variables = [
        ...this.document.variables.filter(({ fieldKey }) => fieldKey !== value),
      ]
      this.setUnsavedChanges(true)
    },
    setDocument(document: IDocumentTemplate) {
      const contactLabel = i18n.global.t('common.contact')
      const senderLabel = i18n.global.t('common.sender')
      const recipientList = uniqBy(
        [
          ...document.recipients,
          {
            isPrimary: true,
            id: 'assignedContact',
            firstName: contactLabel,
            entityName: RecipientEntityTypeEnum.CONTACTS,
            lastName: '',
            contactName: contactLabel,
            email: '',
            hasCompleted: false,
            signingOrder: 1,
            shareLink: '',
            country: '',
            imgUrl: '',
          },
          {
            isPrimary: false,
            id: DOCUMENT_SENDER_ID,
            firstName: senderLabel,
            entityName: RecipientEntityTypeEnum.USERS,
            lastName: '',
            contactName: senderLabel,
            email: '',
            hasCompleted: false,
            signingOrder: 2,
            shareLink: '',
            country: '',
            imgUrl: '',
          },
        ],
        recipient => `${recipient.id}-${recipient.entityName}`
      )
      this.document = {
        ...document,
        recipients: recipientList,
      }
    },
    setDocFormPublicLink(link: string) {
      this.docFormPublicLink = link
    },
    setRedirectSettings(settings: RedirectSettings) {
      this.document.redirectSettings = settings
      this.setUnsavedChanges(true)
    },
    setUnsavedChanges(changes: boolean) {
      this.unsavedChanges = changes
    },
    setupApp(params: TemplateBuilderAppStore) {
      this.email = params.email
      this.userId = params.userId
      this.apiKey = params.apiKey
      this.tokenId = params.tokenId
      this.iamToken = params.iamToken
      this.companyId = params.companyId
      this.timeZone = params.timeZone
      this.userName = params.userName
      this.parentBaseUrl = params.parentBaseUrl
      this.handshake = params.handshake
      this.currency = params.currency
      this.isInternal = [
        '5DP4iH6HLkQsiKESj6rh',
        'YuTUZlUtrwBtvmgByZDW',
        'ktapXmiRvMM4pJHyN3b3',
        'pfpm9iFi4k5yvjnstXWU',
      ].includes(params.companyId) // 5DP4iH6HLkQsiKESj6rh (staging) & YuTUZlUtrwBtvmgByZDW (production) & ktapXmiRvMM4pJHyN3b3(production) & pfpm9iFi4k5yvjnstXWU(production)
    },
    updateLoggedInUserData(userData: any) {
      this.userData = userData
    },
    updateRequestRetryCount(count: number) {
      this.requestRetryCount = count
    },
    updateDocumentName(name: string) {
      this.document.name = name
      this.setUnsavedChanges(true)
    },
    refreshToken(token: string) {
      this.tokenId = token
      console.info('refreshToken')
    },
    refreshIamToken(token: string) {
      this.iamToken = token
      console.info('refreshIamToken')
    },
    updateDocumentOption(
      key: keyof CustomDocumentFields,
      value: CustomDocumentFields[keyof CustomDocumentFields]
    ) {
      this.document[key] = value
      this.setUnsavedChanges(true)
    },
    updateLocale(locale: string) {
      this.locale = locale
    },
    toggleDirectPayment(value: boolean) {
      this.document.enableDirectPayment = value
      if (value === true) {
        this.document.enableAutoSendInvoice = true
      }
      this.setUnsavedChanges(true)
    },
    toggleAutoSendInvoice(value: boolean) {
      this.document.enableAutoSendInvoice = value
      this.setUnsavedChanges(true)
    },
    togglePaymentLiveMode(value: boolean) {
      this.document.paymentLiveMode = value
      this.setUnsavedChanges(true)
    },
    updateDocumentNotificationSenderSettings(
      value:
        | {
            sender?: { fromEmail?: string; fromName?: string }
          }
        | undefined
    ) {
      if (!this.document.notificationSettings)
        this.document.notificationSettings = {}

      delete this.document.notificationSettings['value']
      if (isEmpty(value) || isEmpty(value?.sender)) {
        this.document.notificationSettings.sender = {}
        this.setUnsavedChanges(true)
        return
      }

      if (isNil(this.document.notificationSettings.sender)) {
        this.document.notificationSettings.sender = {}
      }

      if (!isNil(value?.sender?.fromName)) {
        this.document.notificationSettings.sender.fromName =
          value.sender.fromName
      }

      if (!isNil(value?.sender?.fromEmail)) {
        this.document.notificationSettings.sender.fromEmail =
          value.sender.fromEmail
      }

      this.setUnsavedChanges(true)
    },
    updateDocumentNotificationReceiveSettings(
      value:
        | {
            receive?: { subject?: string; templateId?: string }
          }
        | undefined
    ) {
      if (!this.document.notificationSettings)
        this.document.notificationSettings = {}

      if (!this.document.notificationSettings.receive) {
        this.document.notificationSettings.receive = {}
      }

      if (!isNil(value?.receive?.subject)) {
        this.document.notificationSettings.receive.subject =
          value.receive.subject
      }

      if (!isNil(value?.receive?.templateId)) {
        this.document.notificationSettings.receive.templateId =
          value.receive.templateId
      }

      this.setUnsavedChanges(true)
    },
    updateDocumentEmailAttachments(attachments: IDocumentAttachment[]) {
      this.document.emailAttachments = attachments
      this.setUnsavedChanges(true)
    },
    getSettingsErrors() {
      const errors: string[] = []
      const rawDocument = toRaw(this.document)
      if (
        !rawDocument.notificationSettings ||
        isEmpty(rawDocument.notificationSettings)
      )
        return []

      /**
       * Nested insert is introduced with _id property which needs to be removed to check if settings object is empty.
       * This is introduced if the user enables settings and disables it again
       */
      delete rawDocument.notificationSettings['_id']

      if (
        !isEmpty(rawDocument.notificationSettings) &&
        !isEmpty(rawDocument.notificationSettings.sender) &&
        !isNil(rawDocument.notificationSettings.sender?.fromName) &&
        !rawDocument.notificationSettings.sender?.fromName
      ) {
        errors.push('Sender Name is required')
      }

      const specialCharRegex = /[^a-zA-Z0-9\s]/
      if (
        !isEmpty(rawDocument.notificationSettings) &&
        rawDocument.notificationSettings.sender?.fromName
      ) {
        if (
          specialCharRegex.test(
            rawDocument.notificationSettings.sender?.fromName
          )
        ) {
          errors.push('Name format is incorrect')
        }
      }

      if (
        !isEmpty(rawDocument.notificationSettings) &&
        !isEmpty(rawDocument.notificationSettings.sender) &&
        !isNil(rawDocument.notificationSettings.sender?.fromEmail) &&
        !rawDocument.notificationSettings.sender?.fromEmail
      ) {
        errors.push('Email is required')
      }
      if (
        !isEmpty(rawDocument.notificationSettings) &&
        rawDocument.notificationSettings.sender?.fromEmail &&
        !validateEmail(rawDocument.notificationSettings.sender?.fromEmail)
      ) {
        errors.push('Invalid Email Format')
      }

      if (
        !isEmpty(rawDocument.notificationSettings) &&
        !isNil(rawDocument.notificationSettings.receive?.subject) &&
        isEmpty(rawDocument.notificationSettings.receive?.subject)
      ) {
        errors.push('Subject is required')
      }

      return errors
    },
    getRedirectUrlSettingErrors() {
      const errors: string[] = []
      if (
        this.document?.redirectSettings &&
        this.document?.redirectSettings?.enableDocumentRedirectUrl
      ) {
        if (!this.document?.redirectSettings?.documentRedirectUrl) {
          errors.push('Document Redirect URL is required')
        }

        const url = /^https?:\/\//i.test(
          this.document?.redirectSettings?.documentRedirectUrl
        )
          ? `${this.document?.redirectSettings?.documentRedirectUrl}`
          : `https://${this.document?.redirectSettings?.documentRedirectUrl}`
        if (
          this.document?.redirectSettings?.documentRedirectUrl &&
          !isValidUrl(url)
        ) {
          errors.push('Invalid URL format')
        }
      }
      return errors
    },
  },
})
